import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import close from "../../assets/Icons/close.png";
import checkedImg from "../../assets/Icons/photo-taken.png";
import dOneCheckedImg from "../../assets/Icons/d-one-photo-taken.jpg"
import error from "../../assets/svg/failed.svg";
import { useNavigate, useParams } from "react-router-dom";
import { clearImages, removeImages } from "../../Store/fullScan";
import { createScan, createStarInsuranceScan, submitScan } from "../../Services/APIs/scan";
import { KYC_ML_ENABLED } from "../../Services/BaseUrl";
import SuccessScreen from "./submitSuccess";
import { clearReports } from "../../Store/reports";
import { clearHealth } from "../../Store/teethHealth";
import { clearQuestionnaire } from "../../Store/questionnaire";
import { generateSteps } from "./steps";
import { FACE_TITLE, isScanComplete, nextCaptureIndex } from "../../Utils/scanProgress";
import {
    buttonStyle,
    company_style_details,
    fontStyle,
    fontWeight,
    companyBucket,
} from "../../Utils/tpscanStyle";
import AlertPopup from "./alertPopup";
import { clearData } from '../../Store/patientKyc';
import { postLogDetails } from '../../Services/APIs/appDetails';
import { createPatientDetails } from '../../Services/APIs/patient';
import client from "../../Services/APIs/config";

// ai/folder/analyze payload field -> capture step title (Scan/steps.js). One source of truth:
// it builds the outgoing payload AND turns the backend's 400 `missing` list back into steps.
const TOOTH_FIELD_TITLES = {
    front_teeth: "Front teeth",
    upper_jaw: "Upper jaw",
    lower_jaw: "Lower jaw",
    left_side: "Left side",
    right_side: "Right side",
};
// Face is kept out of TOOTH_FIELD_TITLES because submit() sends it as a bare basename, not the
// full URL the teeth steps carry - the payload loops must not overwrite that.
const IMAGE_FIELD_TITLES = { ...TOOTH_FIELD_TITLES, face: FACE_TITLE };

const ToothSelector = (props) => {
    const [cameraSide, setCameraSide] = useState(localStorage.getItem("cameraSide") || "front");
    const steps = generateSteps(cameraSide);

    const { setImageSide, setToothPicker } = props;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { images, fullScanS3Folder } = useSelector((state) => state.fullScan);
    const { selectedOptions, reason } = useSelector(
        (state) => state.questionnaire
    );
    const [isLoading, setLoading] = useState(false);
    const [isSubmitted, setSubmitted] = useState(false);
    const [isError, setError] = useState(false);
    const [doneAlert, setAlert] = useState(false);
    const [isSessionTimeout, setSessionTimeout] = useState(false)
    // Step titles the backend reported as missing on the last submit; drives the retake notice.
    const [retakeTitles, setRetakeTitles] = useState([])
    const patientKyc = useSelector((state) => state.patientKyc.patientKyc);
    const handleImageSide = (index) => {
        setImageSide(index);
        setToothPicker(false);
    };

    // The backend rejects a KYC submission that is missing any of the six images (400 with
    // { data: { missing: [...] } }). That fires when a step's upload came back without an
    // imageName: the local entry still exists, so the picker shows a tick and isScanComplete
    // passes, but the payload carries no path for it. Drop those entries so the picker is
    // honest again and the patient can retake, instead of the generic "AI is having
    // difficulty" dialog, which is neither true nor actionable here.
    // Returns true when it handled the error, false to fall through to the generic dialog.
    const handleMissingImages = (error) => {
        // ResponseService nests the payload under `data`, hence data.data.
        const missing = error?.response?.data?.data?.missing;
        if (!Array.isArray(missing) || !missing.length) return false;

        const missingTitles = missing.map((field) => IMAGE_FIELD_TITLES[field]).filter(Boolean);
        if (!missingTitles.length) return false;

        dispatch(removeImages(missingTitles));
        setRetakeTitles(missingTitles);
        return true;
    };

    const submit = async () => {
        setLoading(true);
        setRetakeTitles([]);

        // Check for demo email - bypass API calls and proceed with positive flow
        const storedEmail = localStorage.getItem('insuranceLoginEmail');
        if (storedEmail && storedEmail.toLowerCase() === 'demo@toothlens.com') {
            // Simulate loading
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Clean up and redirect
            dispatch(clearImages());
            dispatch(clearData());
            localStorage.setItem("isScanCompleted", true);
            localStorage.removeItem("reloaded-star");
            localStorage.removeItem("tl-star-user");
            localStorage.removeItem("reloaded");
            
            // Check if current URL contains "group" and navigate accordingly
            const currentPath = window.location.pathname;
            if (currentPath.includes("/group/")) {
                navigate(`/group/${localStorage.getItem("company")}/${localStorage.getItem("cameraSide")}/thanks`);
            } else {
                navigate(`/${localStorage.getItem("company")}/${localStorage.getItem("cameraSide")}/thanks`);
            }
            return;
        }
        
        const isStarInsuranceFlow = localStorage.getItem("scanType")
        const starInsuranceUser = isStarInsuranceFlow ? JSON.parse(localStorage.getItem("tl-star-user") || "{}") : {};
        const face_url = images.find((eachObj) => eachObj.title === "Face")?.file;

        const starInsuranceData = {
            face: face_url?.split("/").pop(),
            // Name/phone the patient just entered live in the Redux patientKyc store (customerName
            // /mobile) — the same source used to build the patient-details payload below. The old
            // tl-star-user localStorage is never written, so it always yielded undefined and left
            // the KYC-submission email blank. Fall back to it only if patientKyc is somehow empty.
            name: patientKyc?.customerName || starInsuranceUser.name,
            phone_number: patientKyc?.mobile || starInsuranceUser.phone_number,
        }
        for (const key in TOOTH_FIELD_TITLES) {
            images.forEach((eachObj) => {
                if (TOOTH_FIELD_TITLES[key] === eachObj.title) {
                    starInsuranceData[key] = eachObj.file;
                }
            });
        }
        const info = {
            uid: JSON.parse(localStorage.getItem('usertpi') || "{}").usertpi,
            folderName: JSON.parse(localStorage.getItem('usertpi') || "{}").usertpi,
            company: JSON.parse(localStorage.getItem("companyBucket") || "{}").companyBucket,
            ...starInsuranceData,
            is_group_patient: window.location.href.includes('group')
        }

        postLogDetails({
            uid: info.uid,
            app: info.company,
            payLoad: `KYC_ML_ENABLED = ${KYC_ML_ENABLED} -> ${KYC_ML_ENABLED ? "running folder ML (full flow)" : "skipping folder ML (KYC-only)"}`
        });

        if (localStorage.getItem("scanType") && !KYC_ML_ENABLED) {
            // KYC (star) flow with ML disabled: persist a stub report row (image paths + patient
            // info) WITHOUT running the folder-level ML/annotation. skip_ml short-circuits the
            // backend before the ML call, so there is no PDF/report_score. The KYC save then
            // happens in starInsuranceReport(). Flip KYC_ML_ENABLED=true to fall through to the
            // full-ML path below.
            await submitScan({ ...info, client: localStorage.getItem("company"), skip_ml: true }).then(async () => {
                await starInsuranceReport();
            }).catch((error) => {
                setLoading(false);
                if (!handleMissingImages(error)) setError(true);
            });
        } else {
            // Full-ML path: star flow with KYC_ML_ENABLED=true, plus the non-star fallback.
            // Runs the folder ML; createReport() then routes star -> starInsuranceReport(report)
            // (tl-star/store + patient-details) or non-star -> tpReport(report).
            await submitScan(info).then(async (response) => {
                if (localStorage.getItem("usertpi")) {
                    localStorage.setItem("tpscore", JSON.stringify(response.data?.data?.data));
                }
                const { images, ...mlResults } = response?.data?.data?.data;
                const { pdf_url, pdf_without_annotation, name, ...report_score } = mlResults;
                localStorage.setItem("pdf", `${name}.pdf`);
                createReport({ pdf_url, pdf_without_annotation, report_score, ...starInsuranceData });
            }).catch((error) => {
                setLoading(false);
                setError(true);
            });
        }
    };

    const createReport = async (data) => {
        const isGroupPatient = window.location.href.includes('group');
        const employeeData = isGroupPatient ? JSON.parse(localStorage.getItem('employeeData') || '{}') : {};
        
        const report = {
            uid: JSON.parse(localStorage.getItem('usertpi') || "{}").usertpi,
            company: JSON.parse(localStorage.getItem("companyBucket") || "{}").companyBucket,
            client: localStorage.getItem("company"),
            is_full_scan: true,
            user_looking_for: selectedOptions,
            reason,
            is_group_patient: isGroupPatient,
            ...(isGroupPatient && employeeData._id && { wallet_id: employeeData._id }),
            ...data
        };
        for (const key in IMAGE_FIELD_TITLES) {
            images.forEach((eachObj) => {
                if (IMAGE_FIELD_TITLES[key] === eachObj.title) {
                    report[key] = eachObj.file;
                }
            });
        }

        localStorage.getItem("scanType") ? starInsuranceReport(report) : tpReport(report);

    };
    const base64ToFile = (base64Data, fileName, mimeType = "image/jpeg") => {
        const byteString = atob(base64Data.split(',')[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new File([ab], fileName, { type: mimeType });
    };

    const starInsuranceReport = async (report) => {
        try {
            setLoading(true);

            const uid = JSON.parse(localStorage.getItem("usertpi"))?.usertpi;
            const company = JSON.parse(localStorage.getItem("companyBucket"))?.companyBucket;
            
            // Get group patient and employee data
            const isGroupPatient = window.location.href.includes('group');
            const employeeData = isGroupPatient ? JSON.parse(localStorage.getItem('employeeData') || '{}') : {};

            // Prepare FormData first
            const formData = new FormData();
            for (const key in patientKyc) {
                const value = patientKyc[key];
                if (key === "aadhaarCard" && typeof value === "string" && value.startsWith("data:image")) {
                    formData.append("aadhaarCard", base64ToFile(value, "aadhaar.jpg"));
                } else if (key === "panCard" && typeof value === "string" && value.startsWith("data:image")) {
                    formData.append("panCard", base64ToFile(value, "pan.jpg"));
                } else if (key === "aadhaarCardBack" && typeof value === "string" && value.startsWith("data:image")) {
                    formData.append("aadhaarCardBack", base64ToFile(value, "pan.jpg"));
                }
                else if (value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            }
            formData.append("uid", uid);
            formData.append("company", company);
            formData.append("client", localStorage.getItem("company"));

            formData.append("is_group_patient", isGroupPatient);
            if (isGroupPatient && employeeData._id) {
                formData.append("wallet_id", employeeData._id);
            }

            if (KYC_ML_ENABLED) {
                // Full-ML flow: save the report (tl-star/store, which emails the PDF) and the
                // KYC details together, as in the original behaviour.
                const { is_full_scan, reason, user_looking_for, ...rest } = report;
                await Promise.all([
                    createStarInsuranceScan(rest),
                    createPatientDetails(formData)
                ]);
            } else {
                // KYC flow: only save patient/KYC details. The report row was already persisted
                // as a stub by submitScan(skip_ml); no tl-star/store (no ML PDF/report_score,
                // no PDF email — the backend sends a lightweight KYC notification instead).
                await createPatientDetails(formData);
            }

            // Clean up and redirect
            dispatch(clearImages());
            dispatch(clearData());
            localStorage.setItem("isScanCompleted", true);
            localStorage.removeItem("reloaded-star");
            localStorage.removeItem("tl-star-user");
            
            // Check if current URL contains "group" and navigate accordingly
            const currentPath = window.location.pathname;
            if (currentPath.includes("/group/")) {
                navigate(`/group/${localStorage.getItem("company")}/${localStorage.getItem("cameraSide")}/thanks`);
            } else {
                navigate(`/${localStorage.getItem("company")}/${localStorage.getItem("cameraSide")}/thanks`);
            }
        } catch (err) {
            console.error("Submission Error:", err);
            setLoading(false);
            
            // Check if it's a 409 error (Patient already exists)
            if (err.response?.status === 409 || err.response?.data?.code === 409) {
                setSessionTimeout(true);
            } else {
                setError(true);
            }
        }
    };


    const tpReport = async (report) => {
        await createScan(report).then((response) => {
            localStorage.setItem("response", JSON.stringify(response?.data?.data?.report));
            dispatch(clearImages());
            localStorage.setItem("scoreCard", true);
            localStorage.setItem("isScanCompleted", true)
            localStorage.removeItem("reloaded")
            localStorage.getItem("isAutomated") ? navigate("/selfie/thanks") : setSubmitted(true)
            setLoading(false);
        }).catch((error) => {
            setLoading(false);
            setError(true);
        });
    }

    const SessionTimeoutUI = () => {
        const handleRedirect = () => {
            localStorage.clear();
            window.location.href = "https://portal.toothlens.com";
        };

        return (
            <div className="h-full absolute top-0 z-20 w-full flex justify-center items-center bg-black/[0.5] backdrop-blur-md">
                <div className="bg-white w-11/12 max-w-md flex flex-col items-center p-8 rounded-2xl shadow-2xl">
                    <div className="bg-amber-50 rounded-full p-4 mb-4">
                        <svg 
                            className="w-16 h-16 text-amber-500" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                            />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-3">
                        Session Expired
                    </h2>
                    {/* <p className="text-center text-gray-600 mb-2 leading-relaxed">
                        Your session has expired due to a previous scan already being in progress.
                    </p> */}
                    <p className="text-center text-gray-700 font-medium mb-6">
                        Please start fresh from the portal to continue.
                    </p>
                    <div className="w-full">
                        <button
                            type="button"
                            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white w-full h-12 font-semibold text-base shadow-lg rounded-lg transition-all duration-200 transform hover:scale-105"
                            onClick={handleRedirect}
                        >
                            Return to Portal
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const ErrorUI = () => {
        return (
            <div className="h-full absolute top-0 z-10 w-full flex justify-center items-center bg-black/[0.3] backdrop-blur-sm">
                <div className="bg-white w-3/4 flex flex-col items-center p-5 rounded-md shadow-sm">
                    <div className="text-end w-full">
                        {
                            companyBucket === "done" ?
                                <div className="flex justify-end">
                                    <a
                                        href="https://app.dentistry.one/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: "none", color: "inherit" }}>
                                        <img src={close} alt="close_img" width="26px" />
                                    </a>
                                </div> :
                                <button
                                    type="button"
                                    onClick={() => { setError(false) }}>
                                    <img src={close} className="w-6 h-6" alt="close" />
                                </button>
                        }
                    </div>
                    <img src={error} alt="error" className=" w-40 h-40" />
                    <p className="text-center my-5 font-medium">
                        Sorry!! AI is experiencing some difficulty. Please retry in a moment.
                    </p>
                    {companyBucket !== "done" && <div className=" w-full ">
                        <button
                            type="button"
                            className="bg-black text-white w-full h-11 font-semibold text-base shadow-2xl"
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            Back to home page
                        </button>
                    </div>}
                </div>
            </div>
        );
    };

    const Button = (props) => {
        const { i, name, title } = props;
        const checked = images.find((eachObj) => eachObj.title === title);

        return (
            <>
                {checked ? (
                    <img src={(companyBucket === "done" || companyBucket === "hascompanies") ? dOneCheckedImg : checkedImg} alt="checked" className="w-20 h-20 mx-2 rounded-full" />
                ) : (
                    <button
                        className="w-20 h-20 text-xl text-center bg-white rounded-full shadow mx-2"
                        onClick={() => {
                            handleImageSide(i);
                        }}
                        style={(companyBucket === "done" || companyBucket === "hascompanies") ? { fontSize: "16px" } : {}}
                    >
                        {name}
                    </button>
                )}
            </>
        );
    };
    const doNavigate = () => {
        (companyBucket === "done" || companyBucket === "hascompanies") ? setAlert(true) : window.history.back()
    };

    return (
        <>
            {isSubmitted ? (
                <SuccessScreen isFullScan={true} />
            ) : (
                <div className={(companyBucket == "done" || companyBucket === "hascompanies") ? "bg-custom-gradient h-full flex flex-col" : "h-full flex flex-col bg-zinc-100"}>
                    <div className={localStorage.getItem("cameraSide") ? "text-end py-4 px-3" : "flex items-center py-4 px-3"}>
                        {!localStorage.getItem("cameraSide") && (
                            <p
                                className="text-sm flex-1"
                                style={
                                    (companyBucket === "done" || companyBucket === "hascompanies")
                                        ? { ...fontStyle, fontSize: "14px" }
                                        : { ...fontWeight, ...fontStyle }
                                }
                            >
                                Tap to capture and preview your photo
                            </p>
                        )}
                        <button
                            className={localStorage.getItem("cameraSide") !== "back" ? "ml-auto" : ""}
                            onClick={() => doNavigate()}
                        >
                            <img src={close} className="w-7 h-7" alt="close" />
                        </button>
                    </div>

                    <div
                        className="flex-grow flex flex-col items-center justify-center font-medium"
                        style={
                            (companyBucket == "done" || companyBucket === "hascompanies")
                                ? { ...fontStyle }
                                : { ...fontWeight, ...fontStyle }
                        }
                    >
                        <Button i={4} name={"Upper"} title={steps[4].title} />
                        <div
                            className="flex justify-center my-4"
                            style={
                                (companyBucket == "done" || companyBucket === "hascompanies")
                                    ? { ...fontStyle }
                                    : { ...fontWeight, ...fontStyle }
                            }
                        >
                            <Button i={1} name={"Left"} title={steps[1].title} />
                            <Button i={0} name={"Front"} title={steps[0].title} />
                            <Button i={2} name={"Right"} title={steps[2].title} />
                        </div>
                        <Button i={3} name={"Lower"} title={steps[3].title} />
                    </div>
                    {retakeTitles.length > 0 && (
                        <p className="text-center text-sm text-red-600 px-4 mb-1" style={{ ...fontStyle }}>
                            {`${retakeTitles.join(", ")} ${retakeTitles.length > 1 ? "photos" : "photo"} didn't upload. Please retake to continue.`}
                        </p>
                    )}
                    <div className="flex justify-center items-center mb-3">

                        {/* Offer Submit only when every required image is actually present.
                            The old `images.length >= 5` check counted a duplicate upload as a
                            distinct image and let a scan submit with a step missing. When one
                            is missing we send the patient back to it instead - the picker has
                            no circle for "Face", so hiding Submit alone would dead-end them. */}
                        {isScanComplete(steps, images) ? (
                            <button
                                style={
                                    (companyBucket == "done" || companyBucket === "hascompanies")
                                        ? {
                                            ...buttonStyle,
                                            ...fontStyle,
                                            ...fontWeight,
                                            borderRadius: "30px",
                                        }
                                        : { ...buttonStyle, ...fontStyle, ...fontWeight }
                                }
                                type="button"
                                className="w-40 h-11 bg-black text-white font-semibold m-2 rounded"
                                onClick={submit}
                                disabled={isLoading}
                            >
                                {isLoading ? "Processing..." : "Submit Scan"}
                            </button>
                        ) : (
                            <button
                                style={
                                    (companyBucket == "done" || companyBucket === "hascompanies")
                                        ? {
                                            ...buttonStyle,
                                            ...fontStyle,
                                            ...fontWeight,
                                            borderRadius: "30px",
                                        }
                                        : { ...buttonStyle, ...fontStyle, ...fontWeight }
                                }
                                type="button"
                                className="w-40 h-11 bg-black text-white font-semibold m-2 rounded"
                                onClick={() => handleImageSide(nextCaptureIndex(steps, images))}
                            >
                                Continue scan
                            </button>
                        )}
                    </div>
                    {isError && <ErrorUI />}
                    {isSessionTimeout && <SessionTimeoutUI />}
                    {doneAlert && <AlertPopup setPopup={setAlert} />}
                </div>
            )}
        </>
    );
};

export default ToothSelector;
